import { Icon as BuggyIcon } from '@buggy/shared'
import { Menu } from 'antd'
import scrollTo from 'antd/lib/_util/scrollTo'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FunctionComponent, useState } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { FeatureFlag } from '@/api/user-api'
import AlphaTag from '@/components/common/alpha-tag'
import BetaTag from '@/components/common/beta-tag'
import { Icon } from '@/components/custom-icon'
import {
  createMenuTourStepData,
  createTourStepData,
} from '@/components/tour/utils'
import { SearchMode } from '@/constants/search/search-mode'
import { Page } from '@/hooks/use-authorization/constants'
import routes from '@/hooks/use-authorization/routes'
import useCampaignVisible from '@/hooks/use-campaign-visible'
import useFeatureFlags from '@/hooks/use-feature-flags'
import { usePermissions } from '@/hooks/use-permissions'
import { useSelector } from '@/hooks/use-selector'
import { useIntl } from '@/i18n/hooks/use-intl'
import { TourKey } from '@/store/tour'
import { ClickSideMenuListProperties, ampli } from '@/utils/ampli'

const { SubMenu, Item } = Menu

export enum TabKey {
  Search = 'search',
  GeneralSearch = 'generalSearch',
  AISearch = 'aiSearch',
  PostSearch = 'postSearch',
  CampaignList = 'campaignList',
  Report = 'report',
  Hashtag = 'hashtag',
  HashtagBillboard = 'hashtagBillboard',
  HashtagWatchlist = 'hashtagWatchlist',
  InfluencersRanking = 'influencersRanking',
  CompetitiveAnalyses = 'competitiveAnalyses',
  CompetitiveBrandAnalyses = 'competitiveBrandAnalyses',
  CompetitiveKeywordAnalyses = 'competitiveKeywordAnalyses',
  Directory = 'directory',
  KolManagement = 'kolManagement',
  KolCollections = 'kolCollections',
  PostCollections = 'postCollections',
  PredictiveReport = 'predictiveReport',
}

const tourOpenSubMenuTabKeys: Record<
  Extract<
    TourKey,
    | 'search-kol'
    | 'social-performance'
    | 'marketing-trend'
    | 'predict-competitor'
  >,
  TabKey
> = {
  'search-kol': TabKey.Search,
  'social-performance': TabKey.Search,
  'marketing-trend': TabKey.Hashtag,
  'predict-competitor': TabKey.CompetitiveAnalyses,
}

const getTabKey = (pathname: string): TabKey => {
  const tabKeyMap = {
    [Page.Search]: TabKey.GeneralSearch,
    [Page.AISearch]: TabKey.AISearch,
    [Page.KolManagement]: TabKey.KolManagement,
    [Page.CollectionsKol]: TabKey.KolCollections,
    [Page.CollectionsKolId]: TabKey.KolCollections,
    [Page.CollectionsPost]: TabKey.PostCollections,
    [Page.CollectionsPostId]: TabKey.PostCollections,
    [Page.PredictiveReport]: TabKey.PredictiveReport,
    [Page.PredictiveReportIdList]: TabKey.PredictiveReport,
    [Page.PredictiveReportIdReport]: TabKey.PredictiveReport,
    [Page.CampaignList]: TabKey.CampaignList,
    [Page.CampaignListDetail]: TabKey.CampaignList,
    [Page.CampaignListCreateCampaign]: TabKey.CampaignList,
    [Page.Report]: TabKey.Report,
    [Page.ReportId]: TabKey.Report,
    [Page.HashtagBillboard]: TabKey.HashtagBillboard,
    [Page.HashtagWatchlist]: TabKey.HashtagWatchlist,
    [Page.InfluencersRanking]: TabKey.InfluencersRanking,
    [Page.CompetitiveBrandAnalyses]: TabKey.CompetitiveBrandAnalyses,
    [Page.CompetitiveKeywordAnalyses]: TabKey.CompetitiveKeywordAnalyses,
    [Page.HashtagBillboardId]: TabKey.HashtagBillboard,
    [Page.HashtagWatchlistId]: TabKey.HashtagWatchlist,
    [Page.CompetitiveBrandComprehensiveAnalyses]:
      TabKey.CompetitiveBrandAnalyses,
    [Page.CompetitiveBrandPlatformsAnalyses]: TabKey.CompetitiveBrandAnalyses,
    [Page.CompetitiveMentionStatisticsAnalyses]:
      TabKey.CompetitiveBrandAnalyses,
    [Page.CompetitiveMentionContentAnalyses]: TabKey.CompetitiveBrandAnalyses,
    [Page.CompetitiveKeywordStatisticsAnalyses]:
      TabKey.CompetitiveKeywordAnalyses,
    [Page.CompetitiveKeywordContentAnalyses]: TabKey.CompetitiveKeywordAnalyses,
  }

  for (const path in tabKeyMap) {
    if (pathname === path) {
      return tabKeyMap[path]
    }
  }

  return TabKey.GeneralSearch
}

const getLink = (key: TabKey): Page => {
  const pathMap = {
    [TabKey.GeneralSearch]: Page.Search,
    [TabKey.AISearch]: Page.AISearch,
    [TabKey.PostSearch]: Page.Search,
    collection: Page.CollectionsKol,
    campaignList: Page.CampaignList,
    report: Page.Report,
    hashtagBillboard: Page.HashtagBillboard,
    hashtagWatchlist: Page.HashtagWatchlist,
    influencersRanking: Page.InfluencersRanking,
    [TabKey.CompetitiveBrandAnalyses]: Page.CompetitiveBrandAnalyses,
    [TabKey.CompetitiveKeywordAnalyses]: Page.CompetitiveKeywordAnalyses,
    [TabKey.PredictiveReport]: Page.PredictiveReport,
  }

  return pathMap[key]
}

const getSubmenuKeys = (pathname: string): TabKey[] => {
  const tabKeyMap = {
    [Page.Search]: TabKey.Search,
    [Page.AISearch]: TabKey.Search,
    [Page.HashtagBillboard]: TabKey.Hashtag,
    [Page.HashtagWatchlist]: TabKey.Hashtag,
    [Page.InfluencersRanking]: TabKey.Hashtag,
    [Page.CompetitiveBrandAnalyses]: TabKey.CompetitiveAnalyses,
    [Page.CompetitiveKeywordAnalyses]: TabKey.CompetitiveAnalyses,
    [Page.KolManagement]: TabKey.Directory,
    [Page.CollectionsKol]: TabKey.Directory,
    [Page.CollectionsPost]: TabKey.Directory,
  }

  for (const path in tabKeyMap) {
    if (pathname.includes(path)) {
      return [tabKeyMap[path]]
    }
  }

  return []
}

interface IrmFeatureSiderContentProps {
  isCollapsed: boolean
}

const IrmFeatureSiderContent: FunctionComponent<
  IrmFeatureSiderContentProps
> = ({ isCollapsed }) => {
  const { formatMessage } = useIntl()
  const router = useRouter()
  const searchParams = routes.search.useSearchParams()
  const { tourModalVisible, selectedTour, tourStepAction } = useSelector(
    (state) => state.tour,
  )
  const { withGuestPermission, withAIEnhancedPermission } = usePermissions()
  const { withFeature } = useFeatureFlags()

  const campaignVisible = useCampaignVisible()

  //貼文收尋與網紅搜尋在同一頁透過 query mode 來區分
  const selectedKeys =
    router.pathname === Page.Search &&
    searchParams.data?.mode === SearchMode.Post
      ? [TabKey.PostSearch]
      : [getTabKey(router.pathname)]

  const [openKeys, setOpenKeys] = useState<TabKey[]>(
    getSubmenuKeys(router.pathname),
  )

  const showPredictiveReportEntrance =
    withFeature(FeatureFlag.PredictiveReport) && !withGuestPermission

  const handleOpenChange = (keys: TabKey[]): void => {
    setOpenKeys(keys)
  }

  const handleSelect = ({ key }): void => {
    const featureMap = new Map<TabKey, ClickSideMenuListProperties['feature']>([
      [TabKey.Search, 'Search'],
      [TabKey.KolCollections, 'KOL Collection'],
      [TabKey.PostCollections, 'Post Collection'],
      [TabKey.CampaignList, 'Campaign'],
      [TabKey.Report, 'DeepReport'],
      [TabKey.HashtagBillboard, 'HashtagRanking'],
      [TabKey.HashtagWatchlist, 'HashtagMonitoring'],
      [TabKey.InfluencersRanking, 'SocialAccountRanking'],
      [TabKey.CompetitiveBrandAnalyses, 'CompetitiveBrandAnalysis'],
      [TabKey.CompetitiveKeywordAnalyses, 'CompetitiveKeywordAnalysis'],
      [TabKey.KolManagement, 'Directory'],
      [TabKey.PredictiveReport, 'Proposal'],
    ])

    const feature = featureMap.get(key as TabKey)

    if (feature) {
      ampli.clickSideMenuList({ feature })
    }
  }

  useMount(() => {
    scrollTo(0)
  })

  return (
    <Menu
      mode='inline'
      openKeys={((): TabKey[] => {
        if (tourStepAction === 'open-directory') {
          return [TabKey.Directory]
        }

        if (tourModalVisible && selectedTour in tourOpenSubMenuTabKeys) {
          return [tourOpenSubMenuTabKeys[selectedTour]]
        }

        return isCollapsed ? [] : openKeys
      })()}
      selectedKeys={selectedKeys}
      onOpenChange={handleOpenChange}
      onSelect={handleSelect}
    >
      {!withGuestPermission && (
        <SubMenu
          icon={<Icon type='search' />}
          key={TabKey.Search}
          title={<span>{formatMessage({ id: 'irm:navbar_search' })}</span>}
        >
          <>
            <Item
              {...createMenuTourStepData(TabKey.GeneralSearch)}
              key={TabKey.GeneralSearch}
            >
              <Link href={getLink(TabKey.GeneralSearch)}>
                <span>
                  {formatMessage({
                    id: !withAIEnhancedPermission
                      ? 'search:search_influencer'
                      : 'search:ai_tab',
                  })}
                </span>
              </Link>
            </Item>
            <Item
              {...createTourStepData('search-kol', 6)}
              key={TabKey.PostSearch}
            >
              <Link
                href={routes.search.stringifyUrl({}, { mode: SearchMode.Post })}
              >
                <span>{formatMessage({ id: 'search:search_post' })}</span>
              </Link>
            </Item>
            {!withAIEnhancedPermission && (
              <Item key={TabKey.AISearch}>
                <Link href={getLink(TabKey.AISearch)}>
                  {formatMessage({ id: 'search:ai_tab' })}
                </Link>
              </Item>
            )}
          </>
        </SubMenu>
      )}
      {!withGuestPermission && (
        <SubMenu
          icon={<Icon type='elIconCheckListThin' />}
          key={TabKey.Directory}
          title={
            <span>
              {formatMessage({ id: 'general:navbar_item_asset_catalogs' })}
            </span>
          }
        >
          <Item key={TabKey.KolManagement}>
            <Link href={routes.kolManagement.stringifyUrl({})}>
              {formatMessage({
                id: 'general:navbar_item_kol_asset_catalog',
              })}
            </Link>
          </Item>
          <ItemGroup
            {...createTourStepData('search-kol', 5)}
            {...createTourStepData('social-performance', 5)}
          >
            <Item key={TabKey.KolCollections}>
              <Link href={routes.collectionsKol.stringifyUrl({})}>
                {formatMessage({
                  id: 'general:navbar_item_kol_collections',
                })}
              </Link>
            </Item>
            <Item key={TabKey.PostCollections}>
              <Link href={routes.collectionsPost.stringifyUrl({})}>
                {formatMessage({
                  id: 'general:navbar_item_post_collections',
                })}
              </Link>
            </Item>
          </ItemGroup>
        </SubMenu>
      )}
      {showPredictiveReportEntrance && (
        <Item key={TabKey.PredictiveReport}>
          <Link href={getLink(TabKey.PredictiveReport)}>
            <BuggyIconWrapper>
              <BuggyIcon name='clipboard' />
            </BuggyIconWrapper>
            <span>
              {formatMessage({ id: 'irm:navbar_predictive_report' })}
              <Alpha />
            </span>
          </Link>
        </Item>
      )}
      {!withGuestPermission && campaignVisible && (
        <Item key={TabKey.CampaignList}>
          <Link href={getLink(TabKey.CampaignList)}>
            <Icon type='elIconBullhorn' />
            <span>{formatMessage({ id: 'irm:navbar_campaign_matching' })}</span>
          </Link>
        </Item>
      )}
      <Item {...createMenuTourStepData(TabKey.Report)} key={TabKey.Report}>
        <Link href={getLink(TabKey.Report)}>
          <Icon type='elIconBarChart' />
          <span>{formatMessage({ id: 'irm:navbar_deep_report' })}</span>
        </Link>
      </Item>
      {!withGuestPermission && (
        <SubMenu
          icon={<Icon type='rise' />}
          key={TabKey.Hashtag}
          title={
            <span>
              {formatMessage({ id: 'general:navbar_item_hashtags' })}
              <Beta />
            </span>
          }
        >
          <Item
            key={TabKey.HashtagBillboard}
            {...createMenuTourStepData(TabKey.HashtagBillboard)}
          >
            <Link href={getLink(TabKey.HashtagBillboard)}>
              {formatMessage({ id: 'hashtag:navbar_billboard' })}
            </Link>
          </Item>
          <Item key={TabKey.HashtagWatchlist}>
            <Link href={getLink(TabKey.HashtagWatchlist)}>
              {formatMessage({ id: 'hashtag:navbar_watchlist' })}
            </Link>
          </Item>
          <Item key={TabKey.InfluencersRanking}>
            <Link href={`${getLink(TabKey.InfluencersRanking)}?from=sideMenu`}>
              {formatMessage({ id: 'general:social_account_ranking' })}
            </Link>
          </Item>
        </SubMenu>
      )}
      {!withGuestPermission && (
        <SubMenu
          icon={<Icon type='flag' />}
          key={TabKey.CompetitiveAnalyses}
          title={
            <span>
              {formatMessage({
                id: 'general:navbar_item_competitive_analyses',
              })}
            </span>
          }
        >
          <Item
            key={TabKey.CompetitiveBrandAnalyses}
            {...createMenuTourStepData(TabKey.CompetitiveBrandAnalyses)}
          >
            <Link href={getLink(TabKey.CompetitiveBrandAnalyses)}>
              {formatMessage({
                id: 'general:navbar_item_competitive_brand_analyses',
              })}
            </Link>
          </Item>
          <Item
            {...createTourStepData('predict-competitor', 4)}
            key={TabKey.CompetitiveKeywordAnalyses}
          >
            <Link href={getLink(TabKey.CompetitiveKeywordAnalyses)}>
              {formatMessage({
                id: 'general:navbar_item_competitive_keyword_analyses',
              })}
            </Link>
          </Item>
        </SubMenu>
      )}
    </Menu>
  )
}

const ItemGroup = styled(Menu.ItemGroup)`
  .ant-menu-item-group-title {
    display: none;
  }
`

export default IrmFeatureSiderContent

const BuggyIconWrapper = styled.span.attrs({
  className: 'anticon',
})`
  svg {
    vertical-align: middle;
  }
`

const Beta = styled(BetaTag)`
  display: inline-block;
  margin: 0 0 0 4px;
`

const Alpha = styled(AlphaTag)`
  display: inline-block;
  margin: 0 0 0 4px;
`
